import { getBlocks, isBlocked } from '@tymbe/utils/company-blocked-user';
import classNames from 'classnames';
import { FormState, useFormApi, useFormState } from 'informed';
import moment from 'moment';
import { ComponentProps } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import feathersClient from '../../../../apiClient';
import { ErrorAlert, InfoAlert } from '../../../../components/alerts';
import TySelectPerson from '../../../../components/inputs/TySelectPerson';
import { ApplicationData, ApplicationState, PerkId, PersonData, ShiftData } from '../../../../types/TymbeApi';
import { ActionIcon, CustomIcon, FavouriteIcon } from '@tymbe/ui-icons';
import { BadgePill } from '@tymbe/ty-components/indicators';
import { SecondaryButton } from '@tymbe/ui-primitives';
import { useNavigate } from 'react-router-dom';
import ParticipantInfo from './ParticipantInfo';
import { Gender } from '@tymbe/schema/enums';
import { isDeleted } from '../utils';

type ParticipantsOwnProps = {
  current: number,
  total: number,
  onInvite: (form: FormState<ParticipantsFormValues>) => Promise<void>,
  applications?: ApplicationData[],
  exclude?: number[],
  shift: ShiftData,
  disabled?: boolean,
};
type ParticipantsProps = ParticipantsOwnProps & Omit<ComponentProps<'div'>, keyof ParticipantsOwnProps>;

export type ParticipantsFormValues = {
  personSelect: PersonData[]
};

const Participants = ({
  current,
  total,
  onInvite,
  applications,
  exclude = [],
  className = '',
  shift,
  disabled = false,
  ...rest
}: ParticipantsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const participantsFormApi = useFormApi();
  const participantsFormState = useFormState<ParticipantsFormValues>();
  const company = {
    id: shift.company_id,
    branchoffice: shift.branchoffice?.parent_id,
    department: shift.branchoffice_id,
  };

  const handleSubmit = async () => {
    await onInvite(participantsFormState);
    participantsFormApi.reset();
  };
  const { mutateAsync: patchApplication } = useMutation(
    ['patchApplication'],
    async ({ data, appId }:
    { data: Partial<ApplicationData>, appId: number }) => {
      await feathersClient.service('application').patch(appId, data);
    },
    {
      onError: () => {
        ErrorAlert('Při úpravě přihlášky došlo k chybě');
      },
    },
  );

  const { mutateAsync: fetchShiftDetail, isLoading } = useMutation(
    ['nameExport'],
    async (shiftId: number | string) => feathersClient.service('shift').get(shiftId, {
      query: {
        $eager: '[manShift.activeApplication.person.[personData, contact]]',
      },
    }),
  );

  const canInvite =
    participantsFormState.values.personSelect
      ? ((participantsFormState.values.personSelect
        && participantsFormState.values.personSelect.length
        && !participantsFormState
          .values.personSelect.some((item) => isBlocked(getBlocks(item.blockedCompany, company))))
        || (participantsFormState
          .values.personSelect.some((item) => isBlocked(getBlocks(item.blockedCompany, company)))
          && !moment(shift.start_time).isAfter(moment())))
      : false;

  const cancelApplication = async (appId: number) => {
    await patchApplication({ appId, data: { state: ApplicationState.SYSTEM_CANCELED } });
    queryClient.invalidateQueries();
  };

  const onExportClick = async (shiftId: number) => {
      const shiftDetail = await fetchShiftDetail(shiftId);
      if (!shiftDetail || !shiftDetail.manShift) {
        ErrorAlert('Nebylo možné získat detail směny');
        return;
      }

      const confirmedApplications = shiftDetail.manShift.map((ms) => ms.activeApplication)
        ?.filter((app) => app?.state === ApplicationState.CONFIRMED);

      if (confirmedApplications.length === 0) {
        InfoAlert('Tato objednávka neobsahuje přihlášky v Obsazeném stavu');
        return;
      }

      navigate('/name-report', {
        state: { nameReportData: confirmedApplications, nameReportShift: shiftDetail },
      });
    };

    const getCapacityIndicationColor = (capacity = 0) => {
      return capacity > 0 ? '600' : '400'
    }

    const findManShiftsWithPerkId = (perkId: PerkId) => {
      return shift.manShift?.filter(manShift => manShift.perk?.find(perk => perk.id === perkId));
    }

    const findManShiftsWithActiveApplicationBasedOnCompanyJunior = () => {
      return shift.manShift?.filter(manShift => manShift?.activeApplication?.isCompanyJunior && manShift.activeApplication.state === ApplicationState.CONFIRMED);
    }

    const findManShiftsWithActiveApplicationBasedOnGender = (gender: Gender) => {
      return shift.manShift?.filter(manShift => manShift?.activeApplication?.person?.personData?.gender === gender && manShift.activeApplication.state === ApplicationState.CONFIRMED);
    }

    const allManShifts = shift.manShift;

    const manOnlyShifts = findManShiftsWithPerkId(PerkId.MAN_ONLY);
    const womanOnlyShifts = findManShiftsWithPerkId(PerkId.WOMAN_ONLY);
    const juniorShifts = findManShiftsWithPerkId(PerkId.COMPANY_JUNIOR);

    const manOnlyShiftsWithActiveApplications = findManShiftsWithActiveApplicationBasedOnGender(Gender.MALE);
    const womanOnlyShiftsWithActiveApplications = findManShiftsWithActiveApplicationBasedOnGender(Gender.FEMALE);
    const juniorShiftsWithActiveApplications = findManShiftsWithActiveApplicationBasedOnCompanyJunior();

  return (
    <div className={classNames('p-4 rounded-xl border border-secondary-100 gap-3 flex flex-col', className)} {...rest}>
      <div className="flex justify-between text-body-2-xl font-medium text-secondary-600">
        <div className='flex gap-2 items-center'>
          <ActionIcon name="supervisor_account" className='size-4 text-secondary-600' />
          Tymbeři
          <BadgePill variant='info'>{current}/{total}</BadgePill>
          <button onClick={() => onExportClick(shift.id)} className='rounded-full border border-secondary-200 size-6 p-0 flex justify-center items-center'>
            <ActionIcon name="print" className='size-4 text-secondary-400' />
          </button>
        </div>
        <div className='flex gap-2 items-center'>
          <FavouriteIcon name="notifications" className='size-4 text-secondary-600' />
          Pozvánky
          <BadgePill variant='warning'>{applications?.filter((app) => app.invitation && !app.state && isDeleted(app)).length}</BadgePill>
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        <TySelectPerson
          name="personSelect"
          isMulti
          className="cal-person-select border-secondary-400 text-body-2-xl"
          exclude={{ personIds: exclude }}
          filter={{ role: 'tymber' }}
          placeholder="Jméno, email, ID"
          filterOption={(option) => !exclude.includes(option.data?.id)}
          company={company}
          isDisabled={disabled}
        />
        <SecondaryButton
          className='h-8 w-32 items-center justify-center'
          onClick={handleSubmit}
          disabled={!canInvite}
          >
          Pozvat
        </SecondaryButton>
      </div>

      <div className='flex flex-col bg-secondary-transparent-5 rounded-lg w-full p-4 gap-3'>
        <div>
          {/* Objednano a Potvrzeno se doplni po implementaci vytvareni shift (je pro jistotu i ticket) */}
          <span className='text-secondary-600 text-caption-xl font-medium'>{`Objednáno celkem: ${allManShifts?.length}`}</span>
          <div className='flex gap-4 text-secondary-400 font-normal'>
            Minimálně
            <div className='flex gap-1 items-center'>
              <FavouriteIcon name="male" className='size-4' />
              <span className={`font-medium text-secondary-${getCapacityIndicationColor(manOnlyShifts?.length)}`}>{manOnlyShifts?.length}</span>
            </div>
            <div className='flex gap-1 items-center'>
              <FavouriteIcon name="female" className='size-4' />
              <span className={`font-medium text-secondary-${getCapacityIndicationColor(womanOnlyShifts?.length)}`}>{womanOnlyShifts?.length}</span>
            </div>
            <div className='flex gap-1 items-center'>
              <CustomIcon name="rookie" className='size-4' />
              <span className={`font-medium text-secondary-${getCapacityIndicationColor(juniorShifts?.length)}`}>{juniorShifts?.length}</span>
            </div>
          </div>
        </div>
        <hr className="h-px bg-secondary-transparent-10 border-0"/>
        <div>
          <span className='text-secondary-600 text-caption-xl font-medium'>Potvrzeno</span>
          <div className='flex text-secondary-400 font-normal gap-4'>
            <div className='flex gap-1 items-center'>
              <FavouriteIcon name="male" className='size-4' />
              <span className={`font-medium text-secondary-${getCapacityIndicationColor(manOnlyShiftsWithActiveApplications?.length)}`}>{manOnlyShiftsWithActiveApplications?.length}</span>
              Muži
            </div>
            <div className='flex gap-1 items-center'>
              <FavouriteIcon name="female" className='size-4' />
              <span className={`font-medium text-secondary-${getCapacityIndicationColor(womanOnlyShiftsWithActiveApplications?.length)}`}>{womanOnlyShiftsWithActiveApplications?.length}</span>
              Ženy
            </div>
            <div className='flex gap-1 items-center'>
              <CustomIcon name="rookie" className='size-4' />
              <span className={`font-medium text-secondary-${getCapacityIndicationColor(juniorShiftsWithActiveApplications?.length)}`}>{juniorShiftsWithActiveApplications?.length}</span>
              Nováčci
            </div>
          </div>
        </div>
      </div>
      {applications &&
        <ParticipantInfo applications={applications} onAppCancel={cancelApplication} />
      }
    </div>
  );
};

export default Participants;
